// GauntletRun.tsx, active run view: lobby setup, wave progress, voting
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useMercenary } from '../../../contexts/MercenaryContext';
import { useSocket } from '../../../contexts/SocketContext';
import EmojiPicker from '../TeamBattle/EmojiPicker';
import styles from './GauntletRun.module.css';

interface ChatMessage {
  id?: number;
  mercenaryId?: number | null;
  mercenaryName: string;
  message: string;
  timestamp: string;
  isSystem?: boolean;
}

interface GauntletParticipant {
  id: number;
  mercenaryId: number;
  isReady: boolean;
  hasDroppedOut: boolean;
  currentHealth: number | null;
  currentMaxHealth: number | null;
  surrenderHP: number;
  strategy: string;
  continuationVote: 'continue' | 'surrender' | null;
  mercenary: {
    id: number;
    name: string;
    level: number;
    race: string;
    health: number;
    maxHealth: number;
  };
}

interface GauntletRunData {
  id: number;
  bracket: string;
  status: 'waiting' | 'in_wave' | 'voting' | 'completed' | 'failed';
  currentWave: number;
  creatorMercenaryId: number;
  voteDeadline: string | null;
  participants: GauntletParticipant[];
  accumulatedSilver: number;
  accumulatedXp: number;
}

const BRACKET_ROUND_COST: Record<string, number> = { iron: 20, blood: 30, legend: 40 };
const BRACKET_MAX_PARTY: Record<string, number> = { iron: 4, blood: 4, legend: 5 };

const STRATEGY_GROUPS = [
  { label: 'Balanced',   options: ['Normal', 'Normal: Light Attacks', 'Normal: Heavy Attacks'] },
  { label: 'Aggressive', options: ['Offensive', 'Offensive: Light Attacks', 'Offensive: Heavy Attacks'] },
  { label: 'Cautious',   options: ['Defensive', 'Defensive: Light Attacks', 'Defensive: Heavy Attacks'] },
  { label: 'Reckless',   options: ['Berserk', 'Berserk: Light Attacks', 'Berserk: Heavy Attacks'] },
];

const GauntletRun: React.FC = () => {
  const { runId } = useParams<{ runId: string }>();
  const [run, setRun] = useState<GauntletRunData | null>(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [myStrategy, setMyStrategy] = useState('Normal');
  // surrenderHP stored as integer percentage 0-90 (step 10), sent as fraction on save
  const [mySurrenderHP, setMySurrenderHP] = useState(10);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [waveReports, setWaveReports] = useState<{ wave: number; battleReportId: number }[]>([]);
  const [voteCountdown, setVoteCountdown] = useState<string | null>(null);
  const [waveCooldown, setWaveCooldown] = useState(0);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { mercenary } = useMercenary();
  const { socket } = useSocket();
  const mounted = useRef(true);

  const flash = (msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchRun = useCallback(async () => {
    try {
      const res = await axios.get(`/api/gauntlet/runs/${runId}`);
      if (mounted.current) setRun(res.data);
    } catch {
      flash('Failed to load run data.', false);
    }
    if (mounted.current) setLoading(false);
  }, [runId]);

  useEffect(() => {
    mounted.current = true;
    fetchRun();
    // Fetch persisted chat history
    axios.get(`/api/gauntlet/runs/${runId}/chat`)
      .then(res => { if (mounted.current) setChatMessages(res.data); })
      .catch(() => {});
    // Fetch persisted wave battle reports
    axios.get(`/api/gauntlet/runs/${runId}/reports`)
      .then(res => { if (mounted.current) setWaveReports(res.data); })
      .catch(() => {});
    return () => { mounted.current = false; };
  }, [fetchRun]);

  // Initialize local settings from my participant entry
  useEffect(() => {
    if (!run || !mercenary) return;
    const me = run.participants.find(p => p.mercenaryId === mercenary.id);
    if (me) {
      setMyStrategy(me.strategy);
      // Convert fraction (0-1) to nearest 10% integer for the slider
      const pct = Math.round((me.surrenderHP <= 1 ? me.surrenderHP * 100 : me.surrenderHP) / 10) * 10;
      setMySurrenderHP(Math.min(90, Math.max(0, pct)));
    }
  }, [run, mercenary]);

  // Socket: join the gauntlet room and listen for updates
  useEffect(() => {
    if (!socket || !runId) return;
    socket.emit('join_gauntlet_room', { runId });

    const handleUpdate = (data: any) => {
      if (data?.id || data?.runId === Number(runId)) fetchRun();
    };
    const handleEnded = (data: any) => {
      if (data?.runId === Number(runId)) {
        if (data.battleReportId) {
          setWaveReports(prev => {
            const exists = prev.some(r => r.battleReportId === data.battleReportId);
            if (exists) return prev;
            return [...prev, { wave: data.wave ?? 0, battleReportId: data.battleReportId }];
          });
        }
        // Clean up cooldown entry
        localStorage.removeItem(`gauntlet_cooldown_${runId}`);
        fetchRun();
        if (data.status === 'completed') {
          flash('Gauntlet completed! Congratulations!');
        } else if (data.status === 'failed') {
          flash('The party has fallen. Better luck next time.', false);
        } else if (data.status === 'cancelled') {
          flash('Run was cancelled by the creator.', false);
          setTimeout(() => navigate('/gauntlet', { replace: true }), 2000);
        }
      }
    };
    const handleWaveComplete = (data: any) => {
      if (data?.runId === Number(runId)) {
        if (data.battleReportId) {
          setWaveReports(prev => {
            const exists = prev.some(r => r.battleReportId === data.battleReportId);
            if (exists) return prev;
            return [...prev, { wave: data.wave ?? 0, battleReportId: data.battleReportId }];
          });
          // Store cooldown start time so vote buttons are locked for 2 minutes
          localStorage.setItem(`gauntlet_cooldown_${runId}`, Date.now().toString());
          // Auto-redirect to watch the battle in the 2D viewer
          navigate(`/battle-result/${data.battleReportId}`);
        } else {
          // No report to show, go straight to vote
          fetchRun();
        }
      }
    };


    socket.on('gauntlet_run_update', handleUpdate);
    socket.on('gauntlet_run_ended', handleEnded);
    socket.on('gauntlet_wave_complete', handleWaveComplete);
    socket.on('gauntlet_vote_resolved', handleUpdate);

    // Chat
    const handleChatMsg = (msg: ChatMessage) => {
      setChatMessages(prev => [...prev, msg]);
    };
    socket.on('gauntlet_new_message', handleChatMsg);

    return () => {
      socket.emit('leave_gauntlet_room', { runId });
      socket.off('gauntlet_run_update', handleUpdate);
      socket.off('gauntlet_run_ended', handleEnded);
      socket.off('gauntlet_wave_complete', handleWaveComplete);
      socket.off('gauntlet_vote_resolved', handleUpdate);
      socket.off('gauntlet_new_message', handleChatMsg);
    };
  }, [socket, runId, fetchRun, navigate]);

  // ── Actions ──

  const handleLeave = async () => {
    if (!window.confirm('Leave this run?')) return;
    try {
      await axios.post(`/api/gauntlet/runs/${runId}/leave`);
      navigate('/gauntlet', { replace: true, state: { fromGauntlet: true } });
    } catch (err: any) {
      flash(err.response?.data?.message || 'Failed to leave.', false);
    }
  };

  const handleUpdateSettings = async () => {
    if (!mercenary) return;
    try {
      await axios.put(`/api/gauntlet/runs/${runId}/participant/${mercenary.id}`, {
        strategy: myStrategy,
        surrenderHP: mySurrenderHP / 100,
      });
      // Persist for next run, scoped by mercenary id
      localStorage.setItem(`gauntlet_strategy_${mercenary.id}`, myStrategy);
      localStorage.setItem(`gauntlet_surrenderHP_${mercenary.id}`, String(mySurrenderHP));
      flash('Settings saved.');
    } catch (err: any) {
      flash(err.response?.data?.message || 'Failed to save settings.', false);
    }
  };

  const handleToggleReady = async () => {
    if (!mercenary) return;
    const me = run?.participants.find(p => p.mercenaryId === mercenary.id);
    try {
      await axios.put(`/api/gauntlet/runs/${runId}/participant/${mercenary.id}`, {
        isReady: !me?.isReady,
      });
    } catch (err: any) {
      flash(err.response?.data?.message || 'Failed to update ready status.', false);
    }
  };

  const handleStart = async () => {
    setStarting(true);
    try {
      await axios.post(`/api/gauntlet/runs/${runId}/start`);
      flash('Gauntlet started! Preparing Wave 1...');
      fetchRun();
    } catch (err: any) {
      flash(err.response?.data?.message || 'Failed to start.', false);
    }
    setStarting(false);
  };

  const handleVote = async (vote: 'continue' | 'surrender') => {
    try {
      await axios.post(`/api/gauntlet/runs/${runId}/vote`, { vote });
      flash(`Voted to ${vote}.`);
    } catch (err: any) {
      flash(err.response?.data?.message || 'Failed to vote.', false);
    }
  };

  const handleSendChat = () => {
    const trimmed = chatInput.trim();
    if (!trimmed || !socket || !mercenary) return;
    socket.emit('gauntlet_chat_message', { runId: Number(runId), message: trimmed, mercenaryId: mercenary.id });
    setChatInput('');
  };

  const handleChatKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSendChat();
  };

  const renderMessageText = (text: string): React.ReactNode[] => {
    const result: React.ReactNode[] = [];
    const re = /:([0-9]+):/g;
    let lastIndex = 0;
    let match;
    while ((match = re.exec(text)) !== null) {
      if (match.index > lastIndex)
        result.push(<span key={`t${lastIndex}`}>{text.slice(lastIndex, match.index)}</span>);
      result.push(<img key={`e${match.index}`} src={`/api/emojis/${match[1]}`} alt={`:${match[1]}:`} className={styles.chatEmoji} />);
      lastIndex = re.lastIndex;
    }
    if (lastIndex < text.length)
      result.push(<span key={`t${lastIndex}`}>{text.slice(lastIndex)}</span>);
    return result;
  };

  // Voting countdown timer
  useEffect(() => {
    if (countdownRef.current) clearInterval(countdownRef.current);
    if (!run || run.status !== 'voting' || !run.voteDeadline) {
      setVoteCountdown(null);
      return;
    }
    const tick = () => {
      const remaining = new Date(run.voteDeadline!).getTime() - Date.now();
      if (remaining <= 0) {
        setVoteCountdown('Time\'s up');
        if (countdownRef.current) clearInterval(countdownRef.current);
        return;
      }
      const totalSec = Math.floor(remaining / 1000);
      const h = Math.floor(totalSec / 3600);
      const m = Math.floor((totalSec % 3600) / 60);
      const s = totalSec % 60;
      const pad = (n: number) => String(n).padStart(2, '0');
      setVoteCountdown(h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`);
    };
    tick();
    countdownRef.current = setInterval(tick, 1000);
    return () => { if (countdownRef.current) clearInterval(countdownRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run?.status, run?.voteDeadline]);

  // 2-minute wave cooldown, prevents voting immediately after returning from battle report
  useEffect(() => {
    if (!run || run.status !== 'voting') {
      setWaveCooldown(0);
      return;
    }
    const stored = localStorage.getItem(`gauntlet_cooldown_${runId}`);
    if (!stored) { setWaveCooldown(0); return; }
    const startTime = parseInt(stored);
    const COOLDOWN_MS = 2 * 60 * 1000; // 2 minutes
    const tick = () => {
      const remaining = Math.max(0, COOLDOWN_MS - (Date.now() - startTime));
      setWaveCooldown(remaining);
      if (remaining <= 0) localStorage.removeItem(`gauntlet_cooldown_${runId}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [run?.status, runId]);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // ── Render ──

  if (loading) return <div className={styles.loading}>Loading run…</div>;
  if (!run) return <div className={styles.loading}>Run not found.</div>;

  const isCreator = mercenary && run.creatorMercenaryId === mercenary.id;
  const me = mercenary ? run.participants.find(p => p.mercenaryId === mercenary.id) : null;
  const allReady = run.participants.every(p => p.isReady || p.hasDroppedOut);
  const hasVoted = me?.continuationVote !== null && me?.continuationVote !== undefined;
  const roundCost = BRACKET_ROUND_COST[run.bracket] ?? '?';

  const statusLabel: Record<string, string> = {
    waiting:   'Waiting for players...',
    in_wave:   `Wave ${run.currentWave} in progress...`,
    voting:    'Vote: Continue or Surrender?',
    completed: 'Gauntlet Complete!',
    failed:    'Run Failed',
  };

  return (
    <div className={styles.container}>
      {toast && <div className={`${styles.toast} ${toast.ok ? styles.toastOk : styles.toastErr}`}>{toast.msg}</div>}

      {/* Header */}
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>
            {run.bracket.charAt(0).toUpperCase() + run.bracket.slice(1)} Gauntlet
          </h2>
          <span className={styles.statusBadge} data-status={run.status}>
            {statusLabel[run.status] ?? run.status}
          </span>
        </div>
        <div className={styles.headerActions}>
          {run.status === 'waiting' && me && (
            <button className={styles.leaveBtn} onClick={handleLeave}>Leave</button>
          )}
          <button className={styles.backBtn} onClick={() => navigate('/gauntlet', { state: { fromGauntlet: true } })}>← Hub</button>
        </div>
      </div>

      {/* Wave progress bar */}
      {run.status !== 'waiting' && (
        <div className={styles.waveBar}>
          {[1, 2, 3, 4, 5].map(w => (
            <div key={w} className={`${styles.waveStep}
              ${w < run.currentWave ? styles.waveDone : ''}
              ${w === run.currentWave ? styles.waveCurrent : ''}
            `}>
              <span>{w}</span>
              {w === 5 && <small>Boss</small>}
            </div>
          ))}
        </div>
      )}

      {/* Wave battle report links */}
      {waveReports.length > 0 && (
        <div className={styles.waveReportsSection}>
          {[...waveReports].sort((a, b) => a.wave - b.wave).map(r => (
            <div key={r.battleReportId} className={styles.battleReportBanner}>
              <span>⚔ Wave {r.wave} battle —</span>
              <Link to={`/battle-result/${r.battleReportId}`} className={styles.battleReportLink}>
                View Battle Report
              </Link>
            </div>
          ))}
        </div>
      )}

      {/* Participant list */}
      {(() => {
        const maxParty = BRACKET_MAX_PARTY[run.bracket] ?? 4;
        return (
          <div className={styles.participantList}>
            <h3>Party ({run.participants.length}/{maxParty})</h3>
            {run.participants.map(p => {
              const hpPct = p.currentHealth === null
                ? 100
                : Math.min(100, Math.max(0, Math.round((p.currentHealth / (p.currentMaxHealth || p.mercenary.maxHealth || 1)) * 100)));
              const hpColor = hpPct > 60 ? '#4caf72' : hpPct > 30 ? '#c9a227' : '#c0392b';
              return (
                <div key={p.id} className={`${styles.participant} ${p.hasDroppedOut ? styles.droppedOut : ''}`}>
                  <div className={styles.participantRow}>
                    <div className={styles.participantName}>
                      <Link to={`/mercenary/${p.mercenary.id}`} className={styles.nameLink}>
                        {p.mercenary.name}
                      </Link>
                      <small> Lv.{p.mercenary.level} {p.mercenary.race}</small>
                      {p.hasDroppedOut && <span className={styles.droppedBadge}>Dropped</span>}
                    </div>
                    {run.status === 'waiting' && (
                      <span className={p.isReady ? styles.ready : styles.notReady}>
                        {p.isReady ? 'Ready' : 'Not Ready'}
                      </span>
                    )}
                    {run.status === 'voting' && !p.hasDroppedOut && (
                      <span className={p.continuationVote ? styles.ready : styles.notReady}>
                        {p.continuationVote ? '✓ Ready' : 'Waiting...'}
                      </span>
                    )}
                  </div>
                  <div className={styles.hpBarWrap}>
                    <div className={styles.hpBarFill} style={{ width: `${hpPct}%`, background: hpColor }} />
                  </div>
                  <span className={styles.hpLabel}>{hpPct}%</span>
                </div>
              );
            })}
            {Array.from({ length: Math.max(0, maxParty - run.participants.length) }).map((_, i) => (
              <div key={`empty-${i}`} className={styles.emptySlot}>
                <span>Empty slot</span>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Settings panel (waiting phase, own settings) */}
      {run.status === 'waiting' && me && !me.hasDroppedOut && (
        <div className={styles.settingsPanel}>
          <h3>My Settings</h3>
          <div className={styles.prefRow}>
            <label className={styles.prefLabel}>Combat Strategy</label>
            <div className={styles.selectWrapper}>
              <select className={styles.prefSelect} value={myStrategy} onChange={e => setMyStrategy(e.target.value)}>
                {STRATEGY_GROUPS.map(g => (
                  <optgroup key={g.label} label={g.label}>
                    {g.options.map(o => <option key={o} value={o}>{o}</option>)}
                  </optgroup>
                ))}
              </select>
            </div>
          </div>
          <div className={styles.prefRow}>
            <label className={styles.prefLabel}>
              Surrender below <span className={styles.prefValue}>{mySurrenderHP}% HP</span>
            </label>
            <input
              type="range"
              className={styles.prefSlider}
              style={{ '--val': `${(mySurrenderHP / 90) * 100}%` } as React.CSSProperties}
              min={0} max={90} step={10}
              value={mySurrenderHP}
              onChange={e => setMySurrenderHP(Number(e.target.value))}
            />
            <div className={styles.rangeLabels}>
              <span>Fight to death (0%)</span><span>Cautious (90%)</span>
            </div>
          </div>
          <button className={styles.saveSettingsBtn} onClick={handleUpdateSettings}>Save Settings</button>

          <div className={styles.readySection}>
            <p className={styles.roundCostNote}>Entering this gauntlet will cost <strong>{roundCost} rounds</strong>.</p>
            <button
              className={me.isReady ? styles.unreadyBtn : styles.readyBtn}
              onClick={handleToggleReady}
            >
              {me.isReady ? 'Unmark Ready' : 'Mark as Ready'}
            </button>
          </div>
        </div>
      )}

      {/* Start button (creator, all ready) */}
      {run.status === 'waiting' && isCreator && (
        <div className={styles.startSection}>
          {allReady ? (
            <button className={styles.startBtn} onClick={handleStart} disabled={starting}>
              {starting ? 'Starting...' : 'Start Gauntlet'}
            </button>
          ) : (
            <p className={styles.waitingNote}>Waiting for all participants to ready up…</p>
          )}
        </div>
      )}

      {/* Voting phase */}
      {run.status === 'voting' && me && !me.hasDroppedOut && (
        <div className={styles.votePanel}>
          <h3>Wave {run.currentWave} complete!</h3>
          <p>Vote to advance to wave {run.currentWave + 1}. If you surrender, you leave with your earned rewards — others who vote continue will carry on without you.</p>
          {(run.accumulatedSilver > 0 || run.accumulatedXp > 0) && (
            <div className={styles.rewardsTally}>
              <span>Rewards so far:</span>
              {run.accumulatedSilver > 0 && <span><strong>{run.accumulatedSilver.toLocaleString()}</strong> silver</span>}
              {run.accumulatedXp > 0 && <span><strong>{run.accumulatedXp.toLocaleString()}</strong> XP</span>}
            </div>
          )}
          {voteCountdown && (
            <div className={styles.voteTimer}>
              ⏱ Time to vote: <strong>{voteCountdown}</strong>
            </div>
          )}
          {waveCooldown > 0 && (
            <div className={styles.voteTimer}>
              ⏳ You can vote in: <strong>{Math.floor(waveCooldown / 60000)}:{String(Math.floor((waveCooldown % 60000) / 1000)).padStart(2, '0')}</strong>
            </div>
          )}
          {!hasVoted ? (
            <div className={styles.voteButtons}>
              <button className={styles.continueBtn} onClick={() => handleVote('continue')} disabled={waveCooldown > 0}>
                {waveCooldown > 0 ? 'Waiting…' : `Continue to Wave ${run.currentWave + 1}`}
              </button>
              <button className={styles.surrenderBtn} onClick={() => handleVote('surrender')} disabled={waveCooldown > 0}>
                {waveCooldown > 0 ? 'Waiting…' : 'Surrender (take rewards now)'}
              </button>
            </div>
          ) : (
            <>
              <p className={styles.votedNotice}>
                You voted: <strong>{me.continuationVote}</strong>.
              </p>
              {(() => {
                const waiting = run.participants.filter(
                  p => !p.hasDroppedOut && !p.continuationVote
                );
                return waiting.length > 0 ? (
                  <p className={styles.waitingFor}>
                    Waiting for: {waiting.map(p => p.mercenary.name).join(', ')}
                  </p>
                ) : null;
              })()}
            </>
          )}
        </div>
      )}

      {/* In-wave status */}
      {run.status === 'in_wave' && (
        <div className={styles.inWave}>
          <div className={styles.spinner} />
          <p>Wave {run.currentWave} battle in progress…</p>
        </div>
      )}

      {/* Completion */}
      {(run.status === 'completed' || run.status === 'failed') && (
        <div className={styles.endPanel}>
          {run.status === 'completed' ? (
            <p className={styles.victoryMessage}>All 5 waves conquered! The Gauntlet has been beaten!</p>
          ) : (
            <p className={styles.defeatMessage}>The party fell. Partial rewards distributed based on waves completed.</p>
          )}
          <button className={styles.viewReportsBtn} onClick={() => navigate(`/gauntlet/${runId}/report`)}>
            View Wave Reports
          </button>
          <button className={styles.backBtn} onClick={() => navigate('/gauntlet')}>Back to Hub</button>
        </div>
      )}

      {/* Group Chat */}
      <div className={styles.chatPanel}>
        <h3 className={styles.chatTitle}>Party Chat</h3>
        <div className={styles.chatMessages}>
          {chatMessages.length === 0 && (
            <div className={styles.chatSystem}>No messages yet.</div>
          )}
          {chatMessages.map((msg, i) => (
            msg.isSystem
              ? <div key={i} className={styles.chatSystem}>{msg.message}</div>
              : <div key={msg.id ?? i} className={styles.chatMessage}>
                  <span className={styles.chatTime}>{msg.timestamp}</span>
                  {msg.mercenaryId
                    ? <Link to={`/mercenary/${msg.mercenaryId}`} className={styles.chatSender}>{msg.mercenaryName}:</Link>
                    : <span className={styles.chatSender}>{msg.mercenaryName}:</span>
                  }
                  <span className={styles.chatText}>{renderMessageText(msg.message)}</span>
                </div>
          ))}
          <div ref={chatEndRef} />
        </div>
        <div className={styles.chatInputRow}>
          <div className={styles.chatInputWrap}>
            <input
              className={styles.chatInput}
              type="text"
              placeholder="Say something..."
              value={chatInput}
              onChange={e => setChatInput(e.target.value)}
              onKeyDown={handleChatKey}
              maxLength={200}
            />
            <div className={styles.emojiButtonWrap}>
              <button
                className={styles.emojiToggle}
                onClick={() => setShowEmojiPicker(v => !v)}
                type="button"
                title="Emojis"
              >:)</button>
              {showEmojiPicker && (
                <div className={styles.emojiPickerWrapper}>
                  <EmojiPicker onSelect={emoji => {
                    setChatInput(prev => prev + `:${emoji}:`);
                    setShowEmojiPicker(false);
                  }} />
                </div>
              )}
            </div>
          </div>
          <button className={styles.chatSendBtn} onClick={handleSendChat}>Send</button>
        </div>
      </div>
    </div>
  );
};

export default GauntletRun;
